/*
 * Phiếu khám trên server (A22) đối chiếu với phiếu trên máy. Logic thuần.
 * Server giữ phần khám: bác sĩ, mức độ, vùng, ghi chú, yêu cầu chuyên môn, dịch vụ chỉ định, chữ ký.
 * Giá và các trường báo giá khác chỉ nằm trên máy, không gửi lên.
 */
import dayjs from 'dayjs';
import { editQuote, uid, type OfficialQuote, type QLine, type Visit, type ApiSvc } from './logic';
import { apiLine } from './quoteDraft';
import { cardLine, type CardPkg } from './packages';

export interface ServerExamLine { service_id: number | null; package_id: number | null; name: string; unit?: string | null; qty: number }
export interface ServerExamSummary { doctor?: { id: number; name: string } | null; invited_at?: string | null; started_at?: string | null; signed_at?: string | null }
export interface ServerExam extends ServerExamSummary {
  id: number;
  visit_id: number;
  level?: string | null;
  area?: string | null;
  note?: string | null;
  reqs?: string[] | null;
  lines?: ServerExamLine[] | null;
  signature?: string | null;
}

/** Phần của lượt được lưu trên server, dạng dễ so sánh */
export interface ExamState {
  invite: number | null;
  doctor: number | null;
  level: string | null;
  area: string | null;
  note: string | null;
  reqs: string[];
  lines: ServerExamLine[];
  signed: boolean;
}

const ts = (s?: string | null) => (s ? dayjs(String(s).replace(' ', 'T')).valueOf() : undefined);
/** Danh sách mẫu trong code có mã bác sĩ dạng chữ: không gửi lên server */
const numId = (id?: string) => (id && /^\d+$/.test(id) ? Number(id) : null);
const text = (s?: string | null) => (s && s.trim() ? s : null);

export const lineToServer = (l: QLine): ServerExamLine => ({
  service_id: l.apiId ?? null,
  package_id: l.card?.apiId ?? null,
  name: l.name,
  unit: l.unit ?? null,
  qty: l.qty,
});

export const examStateOf = (v: Visit): ExamState => {
  const e = v.quote?.exam;
  return {
    invite: numId(v.invite?.doctor),
    doctor: numId(e?.doctor),
    level: text(e?.level),
    area: text(e?.area),
    note: text(e?.note),
    reqs: [...(e?.reqs ?? [])].sort(),
    lines: (v.quote?.lines ?? []).map(lineToServer),
    signed: !!e?.signedAt,
  };
};

/** Mốc thay đổi của phiếu: danh sách lượt trả các trường này, đổi mới cần tải lại phiếu đầy đủ */
export const examStamp = (e?: ServerExamSummary | null) =>
  (e ? [e.doctor?.id ?? '', e.invited_at ?? '', e.started_at ?? '', e.signed_at ?? ''].join('|') : undefined);

const lineKey = (l: ServerExamLine) => `${l.service_id ?? ''}/${l.package_id ?? ''}/${l.service_id || l.package_id ? '' : l.name}/${l.qty}`;
const sameLines = (a: ServerExamLine[], b: ServerExamLine[]) => a.map(lineKey).sort().join('|') === b.map(lineKey).sort().join('|');

/**
 * Thân PUT: chỉ gồm trường đổi. Phiếu chưa có bác sĩ thì không tạo mới bằng doctor_id rỗng.
 * Phiếu đã ký (trên server và trên máy) thì không gửi gì: dòng đổi lúc này là tư vấn viên thêm sản phẩm, thẻ vào báo giá,
 * không phải bác sĩ sửa chỉ định; gửi lên server sẽ từ chối và đè mất phần tư vấn vừa thêm. Bác sĩ sửa thì huỷ ký trước.
 */
export function examPutBody(prev: ExamState, next: ExamState): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  if (prev.signed && next.signed) return body;
  if (next.doctor !== null && next.doctor !== prev.doctor) body.doctor_id = next.doctor;
  if (next.level !== prev.level) body.level = next.level;
  if (next.area !== prev.area) body.area = next.area;
  if (next.note !== prev.note) body.note = next.note;
  if (next.reqs.join() !== prev.reqs.join()) body.reqs = next.reqs;
  if (!sameLines(prev.lines, next.lines)) body.lines = next.lines;
  return body;
}

/** Phiếu báo giá tự lập khi chưa ai khám thì không tạo phiếu khám: phải có bác sĩ hoặc lời mời */
export const examStateActive = (s: ExamState) => s.doctor !== null || s.invite !== null;

export interface ExamCatalog { services: ApiSvc[]; packages: CardPkg[] }

/** Dòng server sang dòng phiếu: giữ giá đã có trên máy, dòng mới lấy giá từ danh mục */
export function linesFromServer(lines: ServerExamLine[], local: QLine[], cat: ExamCatalog): QLine[] {
  const rest = [...local];
  return lines.map((s) => {
    const i = rest.findIndex((l) => (s.service_id ? l.apiId === s.service_id : s.package_id ? l.card?.apiId === s.package_id : l.name === s.name));
    if (i >= 0) {
      const [hit] = rest.splice(i, 1);
      return { ...hit, qty: s.qty };
    }
    const svc = s.service_id ? cat.services.find((x) => x.id === s.service_id) : undefined;
    if (svc) return apiLine(svc, s.qty);
    const pkg = s.package_id ? cat.packages.find((x) => x.card.apiId === s.package_id) : undefined;
    if (pkg) return { ...cardLine(pkg), qty: s.qty };
    return { id: uid(), svcId: s.service_id ? `api-${s.service_id}` : undefined, apiId: s.service_id ?? undefined, name: s.name, unit: s.unit ?? 'lần', qty: s.qty, price: 0 };
  });
}

/**
 * Phần đổi của lượt khi nhận phiếu từ server. draft lập phiếu báo giá nháp khi máy này chưa có
 * (bác sĩ khám ở máy khác, tư vấn viên mở lượt).
 */
export function applyServerExam(v: Visit, e: ServerExam, cat: ExamCatalog, draft: (x: Visit) => OfficialQuote): Partial<Visit> {
  const invitedAt = ts(e.invited_at);
  const doctor = e.doctor ? String(e.doctor.id) : undefined;
  const invite = invitedAt && doctor ? { doctor, at: invitedAt } : v.invite;
  const out: Partial<Visit> = { invite, examStamp: examStamp(e) };
  if (!e.started_at && !e.signed_at && !e.lines?.length && !v.quote) return out;

  const base = v.quote ?? draft({ ...v, invite });
  const serverLines = e.lines ?? [];
  const keep = !!e.started_at || !!e.signed_at || serverLines.length > 0;
  // Bộ dịch vụ không đổi thì giữ nguyên dòng trên máy, để không mất giá và trạng thái đã gửi khách.
  // Máy này đã có đúng lần ký đó (cùng giờ ký hoặc cùng chữ ký): dòng trên máy là báo giá tư vấn viên đang lập tiếp, giữ nguyên.
  // Bác sĩ huỷ ký, sửa rồi ký lại thì là lần ký khác, nhận dòng mới
  const localSigned = !!e.signed_at && !!base.exam.signedAt
    && (base.exam.signedAt === ts(e.signed_at) || (!!base.exam.sign && base.exam.sign === e.signature));
  const unchanged = !keep || localSigned || sameLines(serverLines, base.lines.map(lineToServer));
  const quote = unchanged ? base : editQuote(base, { lines: linesFromServer(serverLines, base.lines, cat) });
  out.quote = {
    ...quote,
    exam: {
      ...base.exam,
      doctor: doctor ?? base.exam.doctor,
      level: e.level ?? undefined,
      area: e.area ?? undefined,
      note: e.note ?? undefined,
      reqs: e.reqs ?? [],
      signedAt: ts(e.signed_at),
      sign: e.signature ?? undefined,
    },
  };
  return out;
}
